// AU8 (docs/backlog.md): plain-language display helpers for the manager-
// exceptions queue's floor-facing surface (ui/src/main.ts's Manager
// Exceptions badge/sheet on Open Tabs and the ticket-rail banner). Kept in
// a separate, DOM-free module, the same reason ui/src/catalogDisplay.ts and
// ui/src/liquorMatch.ts are: pure functions here are unit-testable with a
// plain node:test file (test/ui-manager-exception-display.test.ts), where
// main.ts itself cannot be imported outside a real DOM (its top-level
// mustFind calls run at import time).
//
// src/domain/managerExceptions.ts's own header is the authoritative
// write-up of what each `kind` means and which are LIVE vs DORMANT today;
// the labels/help text below translate that write-up into the eighth-grade
// language CLAUDE.md's touch-only floor rule requires, never the raw
// `message` column (src/domain/businessDayCapture.ts/payments.ts write
// that field in developer language, cents and order ids included, meant
// for a log or a debug panel, not a bartender or manager mid-shift).

import type {
  ManagerExceptionDto,
  ManagerExceptionHoldFactsDto,
  PaymentResolutionActionDto,
} from "./api.ts";
import { formatCents } from "./money.ts";

const MANAGER_EXCEPTION_KIND_LABELS: Readonly<Record<ManagerExceptionDto["kind"], string>> = {
  business_day_capture_short: "Overnight charge came up short",
  walkout_capture_blocked_by_exception: "Walkout could not collect the card hold",
  // DORMANT as of DEC-029 (managerExceptions.ts's own header): nothing
  // writes this kind today, but the CHECK constraint and this type still
  // allow it, so it gets an honest label rather than a fallback if AU2c
  // ever reopens it.
  capture_shortfall_uncredited: "Card payment did not fully post",
  // PAY14 (docs/build-briefs/PAY14-order-hold-provider-recheck.md, Section
  // 8.1, D3, approved by the ui-ux-designer in round 1 of
  // design/reviews/pay14-order-hold-provider-recheck-2026-09-25.md).
  hold_provider_mismatch: "Card hold from the old payment processor",
  // PAY21 (owner ruling, Kerry, 2026-09-29, "Closed, plus a manager task").
  hold_release_failed_after_close: "Card hold not released after Close",
  // PAY10 (DEC-225, owner ruling "Nightly check", 2026-09-29). DRAFT
  // wording, not yet reviewed by the ui-ux-designer (DEC-194 requires
  // that review before this ships to a real floor); see the PAY10 build
  // brief's own "Outstanding" section.
  unmatched_provider_charge: "Card charge with no ticket record",
  // PAY29 (DEC-226, owner ruling "Auto-release it", 2026-09-29). DRAFT
  // wording, not yet reviewed by the ui-ux-designer (DEC-194); see the
  // PAY29 build brief's own "Outstanding" section.
  hold_increment_release_failed: "Card hold has an extra authorization",
  // PAY34-F1, PAY34-F2. DRAFT wording, not yet reviewed by the ui-ux-designer (DEC-194).
  pending_payment_provider_captured: "Card payment not confirmed",
  failed_payment_provider_captured: "Card charged, not recorded",
  captured_payment_provider_failed: "Card payment failed at processor",
  // SPLIT1 (migration 0293, review N3). DRAFT wording, not yet reviewed by the ui-ux-designer (DEC-194).
  amount_credits_exceed_ticket: "Ticket now worth less than collected",
  // PAY1-S4-F2-F8-F5 slice 3. DRAFT wording, not yet reviewed by the ui-ux-designer (DEC-194).
  settlement_difference: "Stripe settlement needs a look",
  // CASH1-S3. DRAFT wording, not yet reviewed by the ui-ux-designer (DEC-194).
  drawer_open_at_close: "Cash drawer left open",
  // RCPT1-S4. DRAFT wording, not yet reviewed by the ui-ux-designer (DEC-194).
  receipt_capture_failed: "Receipt sale not collected",
  receipt_tip_missed: "Receipt sale captured with no tip",
  // RCPT1-S5. DRAFT wording, not yet reviewed by the ui-ux-designer (DEC-194).
  drawer_kick_ambiguous_printer: "Cash drawer did not open: more than one printer",
  // RCPT1-S2-F1. DRAFT wording, not yet reviewed by the ui-ux-designer (DEC-194).
  printer_password_expired: "Printer password expired",
  // PAY2-S7. DRAFT wording, not yet reviewed by the ui-ux-designer (DEC-194).
  royalty_collection_attention: "Royalty needs attention",
  // LIVETEST1. DRAFT wording, not yet reviewed by the ui-ux-designer (DEC-194).
  test_sale_refund_failed: "Test sale not refunded",
};

/** PAY14 (Section 8.1, D3, approved by the ui-ux-designer in round 1):
 * the queue row's help for a hold_provider_mismatch row. */
export const HOLD_PROVIDER_MISMATCH_HELP =
  "This card hold was placed through the payment processor this location used before, so it can't be charged or released from here. Use Clear hold to see what to do. An open tab can't be paid until the hold is cleared.";

/** PAY21 (owner ruling, Kerry, 2026-09-29): the queue row's help for a
 * hold_release_failed_after_close row. Two sentences, no em dashes, per
 * the ruling's own text: says the release failed and that the hold still
 * needs clearing. */
export const HOLD_RELEASE_FAILED_AFTER_CLOSE_HELP =
  "The card hold release failed after this ticket closed, so the hold is still active. Use Clear hold to record what happened to it.";

/** PAY10 (DEC-225, owner ruling "Nightly check", 2026-09-29). DRAFT
 * wording, not yet reviewed by the ui-ux-designer; see this file's own
 * MANAGER_EXCEPTION_KIND_LABELS comment on the same kind. Two sentences,
 * no em dashes: says what the overnight check found and what to do. */
export const UNMATCHED_PROVIDER_CHARGE_HELP =
  "An overnight check found a completed card charge for this ticket with no matching payment on file. Check the guest's receipt and the payment processor, then resolve this with a note of what you found.";

/** PAY29 (DEC-226, owner ruling "Auto-release it", 2026-09-29). DRAFT
 * wording, not yet reviewed by the ui-ux-designer; see this file's own
 * MANAGER_EXCEPTION_KIND_LABELS comment on the same kind. */
export const HOLD_INCREMENT_RELEASE_FAILED_HELP =
  "This tab's card hold picked up an extra authorization that the system tried to release automatically, but the release failed. Resolve this directly with the payment provider, then record what happened.";

const MANAGER_EXCEPTION_KIND_HELP: Readonly<Record<ManagerExceptionDto["kind"], string>> = {
  business_day_capture_short:
    "The overnight card charge did not cover the whole tab. Collect the rest from the guest, then mark this resolved.",
  // PAY14 (Section 8.2, D9's replacement help, approved by the
  // ui-ux-designer in round 2; shipped in Slice A because Slice A already
  // writes PAY14 Walkout rows, for which the old help was false). True for
  // both writers: AU8's (an earlier exception blocked the capture) and
  // PAY14's (the hold was from the old payment processor).
  walkout_capture_blocked_by_exception:
    "A Walkout on this tab could have collected this amount from the card hold, but the hold could not be used. Decide how to collect it, then resolve this with a note of what was collected.",
  capture_shortfall_uncredited:
    "A card payment collected less than expected and the difference was never credited. Review this before the tab is charged again.",
  // PAY14 (Section 8.1, D3's full help, restored by Slice B now that the
  // Clear hold action exists).
  hold_provider_mismatch: HOLD_PROVIDER_MISMATCH_HELP,
  hold_release_failed_after_close: HOLD_RELEASE_FAILED_AFTER_CLOSE_HELP,
  unmatched_provider_charge: UNMATCHED_PROVIDER_CHARGE_HELP,
  hold_increment_release_failed: HOLD_INCREMENT_RELEASE_FAILED_HELP,
  pending_payment_provider_captured: "Processor shows it paid.",
  failed_payment_provider_captured: "Processor shows it paid.",
  captured_payment_provider_failed: "Processor shows it failed.",
  amount_credits_exceed_ticket:
    "Part of this ticket was paid by amount and it now totals less than was collected. Check the prices and the tax rate, then resolve this with a note of what you found.",
  settlement_difference: "Open the closeout for this date to see the Settlement block.",
  drawer_open_at_close: "Count and close the drawer. It stays open until someone does.",
  receipt_capture_failed:
    "This card sale could not be collected. Collect it from the guest another way, then resolve this with a note.",
  receipt_tip_missed:
    "This sale was collected with no tip. If the guest wrote one on the receipt, settle it with them, then resolve this.",
  drawer_kick_ambiguous_printer:
    "This location has more than one receipt printer, so the drawer was not opened for a cash event. Remove the printer the drawer is not wired to, then resolve this.",
  printer_password_expired:
    "A receipt printer's password is past its maximum age and will stop printing soon. Change the password in Settings, then resolve this.",
  royalty_collection_attention:
    "Read the note. Close any open days of last month, or ask your franchisor to check the royalty debit.",
  test_sale_refund_failed:
    "The $1.00 setup test sale was charged but not refunded. Refund it from your Stripe dashboard, then resolve this with a note.",
};

/** PAY14 (Section 8.1, D5, approved by the ui-ux-designer in round 1;
 * Slice B's text, replacing Slice A's D5-A): the ticket banner's text when
 * EVERY open row on the ticket is a hold_provider_mismatch row (the
 * mixed-kind case keeps the existing text). */
export const HOLD_PROVIDER_MISMATCH_TICKET_BANNER_TEXT =
  "This tab's card hold must be cleared by a manager before Pay will work.";

/** PAY14 (Section 8.1, D2, approved by the ui-ux-designer in round 1):
 * the ticket hold badge's text while the tab's hold was placed through a
 * payment processor this location no longer uses. Whenever the floor
 * shows it, the hold_provider_mismatch row already exists (D2 required
 * change 2). Kept here, beside the row's own texts, so a test compares it
 * (main.ts cannot be imported outside a real DOM). */
export const HOLD_NEEDS_MANAGER_BADGE_TEXT = "Hold needs a manager";

/** PAY14 (Section 8.1, owner answer Q3 = B): the note shown in the hold
 * badge's own slot, on the ticket where a test (simulated) hold was just
 * set aside, until the bartender leaves that ticket. Never a manager
 * queue row; kept here beside D2, its stranded-hold counterpart, so the
 * same test file compares both. */
export const TEST_HOLD_CLEARED_NOTE_TEXT = "Test hold cleared. Take a card at Pay.";

/** PAY14 (Section 8.1, D3), widened by PAY21: the queue row header's
 * amount. For a hold_provider_mismatch or hold_release_failed_after_close
 * row carrying the list's hold facts it is the HELD amount, "{held
 * amount} held" (the stored outstanding_cents, always 0 for both kinds,
 * and the banner's sum, are unchanged); every other row shows
 * outstanding_cents as today. */
export function managerExceptionRowAmountText(
  row: Pick<ManagerExceptionDto, "kind" | "outstanding_cents" | "hold"> & { settlement_finding?: string | null },
): string {
  if (
    (row.kind === "hold_provider_mismatch" || row.kind === "hold_release_failed_after_close") &&
    row.hold !== undefined
  ) {
    return `${formatCents(row.hold.authorizedAmountCents)} held`;
  }
  if (row.kind === "drawer_open_at_close") {
    // AUTOCLOSE1-F1: the row has no amount (outstanding_cents is always 0 and the drawer's expected cash stays behind the
    // blind count), so nothing is drawn rather than "$0.00".
    return "";
  }
  if (row.kind === "settlement_difference") {
    // AUTOCLOSE1-F1: only a "differs" or "dispute" finding carries a figure (raiseSettlementExceptions writes the absolute
    // difference or dispute cents); a failed payout or an unresolved account is written with 0, which is no amount.
    const hasFigure =
      (row.settlement_finding === "differs" || row.settlement_finding === "dispute") && row.outstanding_cents > 0;
    return hasFigure ? formatCents(row.outstanding_cents) : "";
  }
  return formatCents(row.outstanding_cents);
}

/** AUTOCLOSE1-F1: a Cash drawer left open row belongs to a location and a business day, not to a ticket
 * (order_id is null), so the ticket-name lookup has nothing to find. */
export function isDrawerOpenAtCloseRow(row: Pick<ManagerExceptionDto, "kind">): boolean {
  return row.kind === "drawer_open_at_close";
}

/** AUTOCLOSE1-F1: the two kinds that belong to a location and a business day (order_id is null). Their title is
 * "{location}, {day}", not a ticket name. */
export function isLocationDayRow(row: Pick<ManagerExceptionDto, "kind">): boolean {
  return row.kind === "drawer_open_at_close" || row.kind === "settlement_difference";
}

const DRAWER_ROW_WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const DRAWER_ROW_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

/** "Sun Nov 1" from a YYYY-MM-DD business date, read as a calendar date (no time zone shift), or undefined for anything else. */
export function formatBusinessDayShort(businessDate: string | null | undefined): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(businessDate ?? "");
  if (match === null) return undefined;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(date.getTime())) return undefined;
  return `${DRAWER_ROW_WEEKDAYS[date.getUTCDay()]} ${DRAWER_ROW_MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`;
}

/** The title of a Cash drawer left open row: "{location}, {day}". Either part is left out when it is not known; the
 * caller passes the location name only when the row's location is the one it can name. */
export function drawerOpenAtCloseTitle(
  row: { business_date?: string | null; kind?: string },
  locationName: string | undefined,
): string {
  const parts = [locationName?.trim() ?? "", formatBusinessDayShort(row.business_date) ?? ""].filter((part) => part.length > 0);
  return parts.length > 0 ? parts.join(", ") : row.kind === "settlement_difference" ? "Stripe settlement" : "Cash drawer";
}

// ---------------------------------------------------------------------
// PAY14 Slice B: the Clear hold panel (Section 8.1 D4, and Section 8.2
// D4-R, D4-W, D4-C and the Slice B toasts, approved by the ui-ux-designer
// in rounds 1 and 2 of
// design/reviews/pay14-order-hold-provider-recheck-2026-09-25.md). Every
// string below is the approved text word for word; a {placeholder} is
// filled by clearHoldPanelText. "Fallback" is the approved variant for a
// stored provider name with no display name (the raw name is never shown).
// ---------------------------------------------------------------------

/** D4: the row button, in place of "Resolve", for a hold_provider_mismatch row. */
export const CLEAR_HOLD_BUTTON_TEXT = "Clear hold";

/** D4: the panel's confirm action. */
export const CLEAR_HOLD_CONFIRM_TEXT = "Confirm clear hold";

/** D4 line 1. */
export const CLEAR_HOLD_LINE_1 =
  "{amount} is still held on the guest's card through {processor}. It can't be released from here.";
export const CLEAR_HOLD_LINE_1_FALLBACK =
  "{amount} is still held on the guest's card through the payment processor this location used before. It can't be released from here.";

/** PAY21 (owner ruling, Kerry, 2026-09-29): line 1 for a
 * hold_release_failed_after_close row. This hold's own provider still
 * matches what this location charges through (the release attempt failed
 * for an ordinary reason, not a provider mismatch), so line 1 says the
 * release failed and the hold still needs clearing, rather than
 * hold_provider_mismatch's "it can't be released from here." */
export const HOLD_RELEASE_FAILED_LINE_1 =
  "{amount} is still held on the guest's card through {processor}. The release failed when this ticket closed, and the hold still needs to be cleared.";
export const HOLD_RELEASE_FAILED_LINE_1_FALLBACK =
  "{amount} is still held on the guest's card. The release failed when this ticket closed, and the hold still needs to be cleared.";

/** D4 line 2 with no capture_before_at (round 1's approved line). */
export const CLEAR_HOLD_LINE_2_NO_DATE =
  "Release it in your {processor} account, or leave it. Card holds drop off on their own, usually within a week.";
export const CLEAR_HOLD_LINE_2_NO_DATE_FALLBACK =
  "Release it in that processor's own account, or leave it. Card holds drop off on their own, usually within a week.";

/** D4-R: line 2 with a future capture_before_at and no Walkout line. */
export const CLEAR_HOLD_LINE_2_DATE =
  "Release it in your {processor} account, or leave it. It will drop off on its own by {date}.";
export const CLEAR_HOLD_LINE_2_DATE_FALLBACK =
  "Release it in that processor's own account, or leave it. It will drop off on its own by {date}.";

/** D4-R required change 1: line 2 once capture_before_at has passed, with
 * or without the Walkout line and with or without a display name. */
export const CLEAR_HOLD_LINE_2_PAST =
  "It should have dropped off on its own by {date}, so there is likely nothing left to release.";

/** D4 line 3. */
export const CLEAR_HOLD_LINE_3 =
  "Clear hold only takes the hold off this tab. It does not give the guest's money back.";

/** D4-W: between line 1 and line 2 when the order has a Walkout row. */
export const CLEAR_HOLD_WALKOUT_LINE =
  "A Walkout on this tab could have collected {walkout amount} from this hold. You can collect that amount in your {processor} account instead of releasing it.";
export const CLEAR_HOLD_WALKOUT_LINE_FALLBACK =
  "A Walkout on this tab could have collected {walkout amount} from this hold. You can collect that amount in that processor's own account instead of releasing it.";

/** D4-W required change 2: the first sentence only, once capture_before_at
 * has passed. */
export const CLEAR_HOLD_WALKOUT_LINE_PAST =
  "A Walkout on this tab could have collected {walkout amount} from this hold.";

/** D4-W required change 1: line 2 when the Walkout line shows, future date. */
export const CLEAR_HOLD_WALKOUT_LINE_2_DATE =
  "If you don't collect it, release it in your {processor} account or leave it. It will drop off on its own by {date}.";
export const CLEAR_HOLD_WALKOUT_LINE_2_DATE_FALLBACK =
  "If you don't collect it, release it in that processor's own account or leave it. It will drop off on its own by {date}.";

/** D4-W required change 1: line 2 when the Walkout line shows, no date. */
export const CLEAR_HOLD_WALKOUT_LINE_2_NO_DATE =
  "If you don't collect it, release it in your {processor} account or leave it. Card holds drop off on their own, usually within a week.";
export const CLEAR_HOLD_WALKOUT_LINE_2_NO_DATE_FALLBACK =
  "If you don't collect it, release it in that processor's own account or leave it. Card holds drop off on their own, usually within a week.";

/** D4-R required change 2: the picker's visible group label. */
export const CLEAR_HOLD_QUESTION = "What did you do with this hold?";

/** D4-R: the first picker row, holdDisposition "released_at_old_provider". */
export const CLEAR_HOLD_OPTION_RELEASED = "I released this hold in {processor}.";
export const CLEAR_HOLD_OPTION_RELEASED_FALLBACK =
  "I released this hold in that processor's account.";

/** D4-R: the second picker row, holdDisposition "left_to_expire" (the
 * same text with or without a display name). */
export const CLEAR_HOLD_OPTION_LEFT = "I am leaving this hold to drop off on its own.";

/** D4-C: the third picker row, only with a Walkout row on the order,
 * holdDisposition "collected_at_old_provider". */
export const CLEAR_HOLD_OPTION_COLLECTED = "I collected money for the Walkout in {processor}.";
export const CLEAR_HOLD_OPTION_COLLECTED_FALLBACK =
  "I collected money for the Walkout in that processor's account.";

/** D4: the toast after a successful clear. */
export const CLEAR_HOLD_SUCCESS_TOAST = "Hold cleared.";

/** Section 8.2, the Slice B toasts: after a successful clear picked on
 * D4-C's row while the order's Walkout row is still open. */
export const CLEAR_HOLD_WALKOUT_FOLLOW_UP_TOAST =
  "Hold cleared. Next, resolve this tab's Walkout exception with a note of what was collected.";

/** D4: the toast on any failed clear, never the server's raw message. */
export const CLEAR_HOLD_FAILURE_TOAST = "Could not clear this hold. Try again.";

/** D4: the Clear hold panel's Cancel button, "[Cancel]" in the brief's
 * action line. */
export const CLEAR_HOLD_CANCEL_TEXT = "Cancel";

/** The optional note field's aria-label, unchanged by PAY14: the Clear
 * hold panel's note (D4, "the existing note input, unchanged") and the
 * generic Resolve panel's on every kind but the Walkout row (D9). */
export const RESOLUTION_NOTE_OPTIONAL_ARIA_LABEL = "Resolution note, optional";

/** D9 (Section 8.2): the note field's placeholder on a
 * walkout_capture_blocked_by_exception row, in place of "Note (optional)". */
export const WALKOUT_NOTE_PLACEHOLDER = "What was collected (required)";

/** D9: the same field's aria-label, in place of "Resolution note, optional". */
export const WALKOUT_NOTE_ARIA_LABEL = "Resolution note, required: what was collected";

/** PL3-F2-F4: the same field on an unmatched_provider_charge row, whose resolution is final. */
export const UNMATCHED_NOTE_PLACEHOLDER = "Resolution note (required)";
export const UNMATCHED_NOTE_ARIA_LABEL = "Resolution note, required";

export type ClearHoldDisposition =
  "released_at_old_provider" | "left_to_expire" | "collected_at_old_provider";

export interface ClearHoldPanelText {
  line1: string;
  /** D4-W, or null when the order has no Walkout row. */
  walkoutLine: string | null;
  line2: string;
  line3: string;
  question: string;
  /** D4-R's two rows, then D4-C's row when the order has a Walkout row. */
  options: { disposition: ClearHoldDisposition; label: string }[];
}

function fillTemplate(template: string, values: Readonly<Record<string, string>>): string {
  return template.replace(/\{([a-z ]+)\}/g, (whole, key: string) => values[key] ?? whole);
}

/** D4-R's approved date format: weekday, month and day, short ("Fri,
 * Oct 2"), in the location's own time zone. No year and no time. An
 * unknown time zone falls back to UTC rather than failing the panel.
 *
 * UX55: joined with U+00A0 (non-breaking space) between parts, not the
 * formatter's own ordinary space, so the whole date stays one unbreakable
 * run and can never wrap mid-date the way an ordinary space allows
 * ("by Wed," / "Sep 30." across two lines, the backlog row's own
 * example). formatToParts, not the formatted string, so this only
 * touches the LITERAL separators the formatter itself inserts, never a
 * digit or letter inside a weekday/month/day token. */
export function formatClearHoldDate(iso: string, timeZone: string): string {
  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
  };
  const format = (resolvedTimeZone: string): string =>
    new Intl.DateTimeFormat("en-US", { ...options, timeZone: resolvedTimeZone })
      .formatToParts(new Date(iso))
      .map((part) => (part.type === "literal" ? part.value.replace(/ /g, " ") : part.value))
      .join("");
  try {
    return format(timeZone);
  } catch {
    return format("UTC");
  }
}

/** Every line of the Clear hold panel, top to bottom, chosen by Section
 * 8.2's table from the list's hold facts. "Past" means capture_before_at
 * is earlier than `nowMs`, the moment the panel is built. `kind` (PAY21)
 * picks line 1 only: hold_release_failed_after_close's own hold still
 * matches the configured provider, so it says the release failed and the
 * hold still needs clearing rather than hold_provider_mismatch's "it
 * can't be released from here." Every other line, and the disposition
 * options, are unchanged by `kind` -- the same manager clear action
 * resolves either kind. */
export function clearHoldPanelText(
  hold: ManagerExceptionHoldFactsDto,
  nowMs: number = Date.now(),
  kind: ManagerExceptionDto["kind"] = "hold_provider_mismatch",
): ClearHoldPanelText {
  const processor = hold.providerDisplayName;
  const named = processor !== null;
  const values: Record<string, string> = {
    amount: formatCents(hold.authorizedAmountCents),
    processor: processor ?? "",
    "walkout amount":
      hold.walkoutBlockedCents === null ? "" : formatCents(hold.walkoutBlockedCents),
    date:
      hold.captureBeforeAt === null
        ? ""
        : formatClearHoldDate(hold.captureBeforeAt, hold.locationTimeZone),
  };
  const dateState: "none" | "future" | "past" =
    hold.captureBeforeAt === null
      ? "none"
      : new Date(hold.captureBeforeAt).getTime() < nowMs
        ? "past"
        : "future";
  const walkout = hold.walkoutBlockedCents !== null;

  let walkoutLine: string | null = null;
  if (walkout) {
    walkoutLine = fillTemplate(
      dateState === "past"
        ? CLEAR_HOLD_WALKOUT_LINE_PAST
        : named
          ? CLEAR_HOLD_WALKOUT_LINE
          : CLEAR_HOLD_WALKOUT_LINE_FALLBACK,
      values,
    );
  }

  let line2Template: string;
  if (dateState === "past") {
    line2Template = CLEAR_HOLD_LINE_2_PAST;
  } else if (walkout) {
    if (dateState === "future") {
      line2Template = named
        ? CLEAR_HOLD_WALKOUT_LINE_2_DATE
        : CLEAR_HOLD_WALKOUT_LINE_2_DATE_FALLBACK;
    } else {
      line2Template = named
        ? CLEAR_HOLD_WALKOUT_LINE_2_NO_DATE
        : CLEAR_HOLD_WALKOUT_LINE_2_NO_DATE_FALLBACK;
    }
  } else if (dateState === "future") {
    line2Template = named ? CLEAR_HOLD_LINE_2_DATE : CLEAR_HOLD_LINE_2_DATE_FALLBACK;
  } else {
    line2Template = named ? CLEAR_HOLD_LINE_2_NO_DATE : CLEAR_HOLD_LINE_2_NO_DATE_FALLBACK;
  }

  const options: ClearHoldPanelText["options"] = [
    {
      disposition: "released_at_old_provider",
      label: fillTemplate(
        named ? CLEAR_HOLD_OPTION_RELEASED : CLEAR_HOLD_OPTION_RELEASED_FALLBACK,
        values,
      ),
    },
    { disposition: "left_to_expire", label: CLEAR_HOLD_OPTION_LEFT },
  ];
  if (walkout) {
    options.push({
      disposition: "collected_at_old_provider",
      label: fillTemplate(
        named ? CLEAR_HOLD_OPTION_COLLECTED : CLEAR_HOLD_OPTION_COLLECTED_FALLBACK,
        values,
      ),
    });
  }

  const line1Template =
    kind === "hold_release_failed_after_close"
      ? named
        ? HOLD_RELEASE_FAILED_LINE_1
        : HOLD_RELEASE_FAILED_LINE_1_FALLBACK
      : named
        ? CLEAR_HOLD_LINE_1
        : CLEAR_HOLD_LINE_1_FALLBACK;

  return {
    line1: fillTemplate(line1Template, values),
    walkoutLine,
    line2: fillTemplate(line2Template, values),
    line3: CLEAR_HOLD_LINE_3,
    question: CLEAR_HOLD_QUESTION,
    options,
  };
}

/** Section 8.2, the Slice B toasts: the follow-up toast only when the
 * picked row was D4-C's and the order's Walkout row is still open (known
 * from the exception list the client already holds). */
export function clearHoldSuccessToast(
  disposition: ClearHoldDisposition,
  orderId: string,
  openRows: readonly Pick<ManagerExceptionDto, "kind" | "order_id">[],
): string {
  const walkoutRowOpen = openRows.some(
    (row) => row.order_id === orderId && row.kind === "walkout_capture_blocked_by_exception",
  );
  return disposition === "collected_at_old_provider" && walkoutRowOpen
    ? CLEAR_HOLD_WALKOUT_FOLLOW_UP_TOAST
    : CLEAR_HOLD_SUCCESS_TOAST;
}

// ---------------------------------------------------------------------
// PAY34-F1, PAY34-F2: the resolve panel for a payment the processor and our record disagree about. The Clear hold
// panel's own markup (a radio group, the note, Cancel, Confirm), other labels. DRAFT wording for the
// ui-ux-designer (DEC-194). Terse on purpose: labels only.
// ---------------------------------------------------------------------

export const PAYMENT_DISAGREEMENT_KINDS: readonly ManagerExceptionDto["kind"][] = [
  "pending_payment_provider_captured",
  "failed_payment_provider_captured",
  "captured_payment_provider_failed",
];

export function isPaymentDisagreementKind(kind: string): boolean {
  return (PAYMENT_DISAGREEMENT_KINDS as readonly string[]).includes(kind);
}

export interface PaymentResolutionOption {
  action: PaymentResolutionActionDto;
  label: string;
  /** The server refuses this action without a note. */
  noteRequired: boolean;
}

const RECORD_PAYMENT: PaymentResolutionOption = {
  action: "record_captured",
  label: "Record payment",
  noteRequired: false,
};

/** The actions the server accepts for each kind, in picker order (src/domain/pendingPaymentResolution.ts
 * PAYMENT_RESOLUTION_ACTIONS_BY_KIND holds the same sets; a test compares them). */
export function paymentResolutionOptions(kind: string): PaymentResolutionOption[] {
  switch (kind) {
    // B1: this row exists because the processor showed the payment captured, so Mark failed is NOT offered here
    // (the server refuses it for a captured payment in any case). Handled at processor changes no row.
    case "pending_payment_provider_captured":
    case "failed_payment_provider_captured":
      return [
        RECORD_PAYMENT,
        { action: "handled_at_provider", label: "Handled at processor", noteRequired: true },
      ];
    case "captured_payment_provider_failed":
      return [{ action: "acknowledged", label: "Checked", noteRequired: true }];
    default:
      return [];
  }
}

export const PAYMENT_RESOLUTION_QUESTION = "Resolution";
/** Shown in the confirm step while Record payment is picked: the sale lands on the open day, refunds are not checked. */
export const PAYMENT_RESOLUTION_RECORD_LINE = "Recorded on today's open day. Refunds not checked.";
export const PAYMENT_RESOLUTION_CONFIRM_TEXT = "Confirm";
export const PAYMENT_RESOLUTION_NOTE_REQUIRED_PLACEHOLDER = "Note (required)";
export const PAYMENT_RESOLUTION_NOTE_REQUIRED_ARIA_LABEL = "Resolution note, required";
export const PAYMENT_RESOLUTION_FAILURE_TOAST = "Could not resolve.";
export const PAYMENT_RESOLUTION_SUCCESS_TOAST = "Resolved.";

/** PAY35. The closeout list's per-row button, and one terse line per outcome. DRAFT wording for the designer (DEC-194). */
export const CHECK_PAYMENT_LABEL = "Check payment";
export const CHECK_PAYMENT_BUSY_LABEL = "Checking";
export const CHECK_PAYMENT_FAILURE_TOAST = "Could not check.";

export function checkPaymentToast(kind: string): { text: string; tone: "success" | "danger" } {
  switch (kind) {
    case "resolved_failed":
      return { text: "Cleared. The tab can be paid.", tone: "success" };
    case "still_processing":
      return { text: "Still processing. Try again soon.", tone: "danger" };
    case "provider_captured":
      return { text: "Processor shows it paid. See Exceptions.", tone: "danger" };
    case "not_pending":
      return { text: "Already settled.", tone: "success" };
    default:
      return { text: "Processor not reachable. Try again.", tone: "danger" };
  }
}

/** The Confirm button is enabled once an action is picked and, for an action that needs one, a note is typed. */
export function paymentResolutionSatisfied(
  kind: string,
  action: PaymentResolutionActionDto | undefined,
  note: string,
): boolean {
  const option = paymentResolutionOptions(kind).find((candidate) => candidate.action === action);
  return option !== undefined && (!option.noteRequired || note.trim().length > 0);
}

/** D9, PL3-F2-F4: a walkout_capture_blocked_by_exception or unmatched_provider_charge row's note is
 * required, so its Confirm resolve stays disabled until the note holds a non-space character (the
 * server's own rule). Every other kind's note is optional. */
export function resolveNoteRequired(kind: ManagerExceptionDto["kind"]): boolean {
  return kind === "walkout_capture_blocked_by_exception" || kind === "unmatched_provider_charge";
}

/** The required-note field's placeholder and aria-label for a kind (Walkout text, or the unmatched-charge text). */
export function resolveNoteFieldText(kind: ManagerExceptionDto["kind"]): {
  placeholder: string;
  ariaLabel: string;
} {
  if (kind === "unmatched_provider_charge") {
    return { placeholder: UNMATCHED_NOTE_PLACEHOLDER, ariaLabel: UNMATCHED_NOTE_ARIA_LABEL };
  }
  if (resolveNoteRequired(kind)) {
    return { placeholder: WALKOUT_NOTE_PLACEHOLDER, ariaLabel: WALKOUT_NOTE_ARIA_LABEL };
  }
  return { placeholder: "Note (optional)", ariaLabel: RESOLUTION_NOTE_OPTIONAL_ARIA_LABEL };
}

export function resolveNoteSatisfied(kind: ManagerExceptionDto["kind"], note: string): boolean {
  return !resolveNoteRequired(kind) || note.trim().length > 0;
}

const FALLBACK_KIND_LABEL = "Manager exception";
const FALLBACK_KIND_HELP =
  "This tab needs a manager's review before more money moves on it. Ask a manager for help if this is unclear.";

/** Plain-language name for one exception's own `kind`, for the queue row's
 * headline. Falls back to a generic label for a kind this build does not
 * recognize yet, rather than showing the raw snake_case value. */
export function managerExceptionKindLabel(kind: string): string {
  return (MANAGER_EXCEPTION_KIND_LABELS as Record<string, string>)[kind] ?? FALLBACK_KIND_LABEL;
}

/** One or two plain sentences explaining what happened and what a manager
 * should do about it. */
export function managerExceptionKindHelp(kind: string): string {
  return (MANAGER_EXCEPTION_KIND_HELP as Record<string, string>)[kind] ?? FALLBACK_KIND_HELP;
}

/** "How old": "Just now", "N min ago" under an hour, "N hr ago" / "N hr M
 * min ago" at or past an hour. Mirrors main.ts's own formatAge (the Open
 * Tabs aging pill) in shape, worded "ago" instead of "Open" since this
 * describes when the EXCEPTION was raised, not how long a tab has been
 * open. `nowMs` is injectable so a test can pin "now" instead of racing the
 * real clock. */
export function formatManagerExceptionAge(
  createdAtIso: string,
  nowMs: number = Date.now(),
): string {
  const createdAtMs = new Date(createdAtIso).getTime();
  const minutes = Math.max(0, Math.round((nowMs - createdAtMs) / 60_000));
  if (minutes < 1) {
    return "Just now";
  }
  if (minutes < 60) {
    return `${minutes} min ago`;
  }
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder === 0 ? `${hours} hr ago` : `${hours} hr ${remainder} min ago`;
}
